/**
 * Subscription-related type definitions for CiteOps.ai Dashboard
 */

import type { Tables } from './database'
import { isPremiumDomain, OVERRIDE_TIER } from '../lib/domain-override'

/** Available subscription tiers */
export type SubscriptionTier = 'free' | 'pro' | 'team'

/** Stripe subscription statuses */
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'

/** Subscription record from the database */
export interface Subscription {
  id: string
  user_id: string
  stripe_subscription_id: string | null
  stripe_price_id: string | null
  tier: SubscriptionTier
  status: SubscriptionStatus
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  canceled_at: string | null
  trial_start: string | null
  trial_end: string | null
  created_at: string
  updated_at: string
}

/** Ensures the Subscription interface stays in sync with the DB row type */
export type SubscriptionRow = Tables<'subscriptions'>

/** Statuses that indicate an active, usable subscription */
export const ACTIVE_STATUSES: readonly SubscriptionStatus[] = [
  'active',
  'trialing',
] as const

/** Statuses that indicate the subscription needs attention */
export const WARNING_STATUSES: readonly SubscriptionStatus[] = [
  'past_due',
  'incomplete',
] as const

/** Statuses that indicate the subscription is no longer usable */
export const INACTIVE_STATUSES: readonly SubscriptionStatus[] = [
  'canceled',
  'unpaid',
] as const

/**
 * Check whether a subscription status grants access to paid features.
 * Both 'active' and 'trialing' are considered usable.
 */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(status)
}

/**
 * Determine the effective tier based on subscription status.
 * If the subscription is not active, fall back to 'free'.
 *
 * When `email` is provided and belongs to a premium domain
 * (@coinis.com, @coinisconnect.com), returns Team tier regardless
 * of the subscription state.
 */
export function getEffectiveTier(
  subscription: Subscription | null,
  email?: string | null,
): SubscriptionTier {
  // Domain override takes precedence
  if (isPremiumDomain(email)) return OVERRIDE_TIER

  if (!subscription) return 'free'
  if (!isSubscriptionActive(subscription.status)) return 'free'
  return subscription.tier
}
